import { useState, useEffect } from 'react';
import { TextInput, Pressable, StyleSheet } from 'react-native';

import { applyCoupon } from '../../api/coupon';
import Colors from '../../constants/Colors';
import useColorScheme from '../../hooks/useColorScheme';
import { formatPrice } from '../../utils/locations';
import Row, { TransparentRow } from '../Row';
import { TextTag, TextBodySmall, TextBody } from '../StyledText';
import { TransparentView } from '../Themed';

export default function CouponApplier({
  onCouponApplied,
}: {
  onCouponApplied: (
    discount: number | null,
    type: 'percentage' | 'fixed' | null
  ) => void;
}) {
  const theme = useColorScheme();
  const [couponCode, setCouponCode] = useState('');

  const [couponState, setCouponState] = useState<
    | {
        status: 'idle';
      }
    | {
        status: 'loading';
      }
    | {
        status: 'success';
        discount: number;
        discountType: 'percentage' | 'fixed';
      }
    | {
        status: 'error';
        error: string;
      }
  >({ status: 'idle' });

  useEffect(() => {
    if (couponState.status === 'success') {
      onCouponApplied(couponState.discount, couponState.discountType);
    }
  }, [couponState]);

  const handleApplyCoupon = async () => {
    setCouponState({ status: 'loading' });
    try {
      const response = await applyCoupon({ couponCode });
      if (response.status === 'success') {
        setCouponState(response);
      } else {
        setCouponState({ status: 'error', error: response.error });
      }
    } catch (error) {
      console.error(error);
      setCouponState({ status: 'error', error: 'Failed to apply coupon' });
    }
  };

  const handleRemoveCoupon = () => {
    setCouponState({ status: 'idle' });
    setCouponCode('');
    onCouponApplied(null, null);
  };

  return (
    <Row style={styles.row}>
      <TransparentView>
        <TransparentRow
          style={{
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: 4,
          }}
        >
          <TextInput
            placeholder="Coupon code"
            style={{
              margin: 0,
              padding: 0,
              height: 30,
              borderRadius: 6,
              borderWidth: 1,
              borderColor: Colors[theme].lines,
              paddingHorizontal: 8,
            }}
            value={couponCode}
            onChangeText={setCouponCode}
          />
          <Pressable
            style={{
              paddingHorizontal: 8,
              height: 30,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#eee',
              borderRadius: 6,
            }}
            onPress={
              couponState.status === 'success'
                ? handleRemoveCoupon
                : handleApplyCoupon
            }
          >
            {couponState.status === 'loading' ? (
              <TextTag>Checking...</TextTag>
            ) : couponState.status === 'success' ? (
              <TextTag>Remove</TextTag>
            ) : (
              <TextTag>Apply</TextTag>
            )}
          </Pressable>
        </TransparentRow>
        {couponState.status === 'error' ? (
          <TextBodySmall style={{ color: Colors[theme].error }}>
            {couponState.error}
          </TextBodySmall>
        ) : null}
      </TransparentView>
      <TextBody>
        {couponState.status === 'success'
          ? `${formatPrice(-couponState.discount)}`
          : null}
      </TextBody>
    </Row>
  );
}

const styles = StyleSheet.create({
  row: {
    justifyContent: 'space-between',
    marginBottom: 10,
  },
});
